import React, { useEffect, useMemo, useState } from 'react';

/* External */
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

/* Material UI */
import { Container } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';

/* Project */
import { useIsMobileDevice } from 'utils/hooks/isMobileDevice';
import { MODULES_ACTIONS } from 'screens/Modules';
import DeleteModal from 'commons/components/DeleteModal';
import ItemList from 'screens/Processes/components/ItemList';
import Filters from 'screens/Processes/components/Filters';
import EmployeeCostsModal from 'screens/Processes/components/EmployeeCostsModal';
import ProcessForm from './ProcessForm';
import AutoScannModal from './modals/AutoScannModal';
import ReclassifySpeciesDispatchModal from './modals/ReclassifySpeciesDispatchModal';
import { getAccessPolicy, getCurrentSubdomain } from 'utils/accessPolicy';

function ProcessesComponent(props) {
  const {
    classes,
    filter,
    onFilterChange,
    objects,
    filterProcesses,
    clearFilters,
    toggleEmployeeCostsModal,
    toggleEdit,
    toggleDelete,
    processList,
    handleClick,
    handleClose,
    anchorEl,
    processOnClick,
    employeeCostsModal,
    changeEmployeeCostsFile,
    submitEmployeeCosts,
    file,
    updatedProcesses,
    formIsOpen,
    changeControls,
    toggleForm,
    actions,
    formControls,
    edit,
    submit,
    invalidControls,
    deleteIsOpen,
    closeForm,
    submitDelete,
    anchorElId,
    loaders,
    pagination,
    loadingProcess,
    headerList,
    socketId,
    suppliersByProcess,
    hasAutomation,
    device,
    reclassifyModalOpen,
    reclassifyProcess,
    closeReclassifyModal,
    openReclassifyModal,
    submitReclassifySpeciesDispatch,
  } = props;

  const { t } = useTranslation();
  const isMobileDevice = useIsMobileDevice();
  const dispatch = useDispatch();
  const accessPolicy = getAccessPolicy({
    subdomain: getCurrentSubdomain(),
    roleName: props.role?.name,
  });
  const { canCreate } = accessPolicy.processes;
  const [autoScannerIsOpen, setAutoScannerIsOpen] = useState(false);
  const [stepData, setStepData] = useState(null);
  const accountId = Object.values(objects?.accounts || {})[0]?.id || null;

  useEffect(() => {
    dispatch({
      type: MODULES_ACTIONS.GET_STATE_FROM_API,
    });
  }, [dispatch]);

  const handleAutoScan = (data) => {
    if (actions.executeAutomaticTransition) {
      actions.executeAutomaticTransition(
        {
          ...data,
          accountId,
        },
        (err, response) => {
          if (!err && response) {
            setStepData(response);
          }
        },
        device,
      );
    }
  };

  const refreshProcessesAfterSuccess = () => {
    actions.getStateFromApi({ ...filter, hasAutomation: true }, { ...pagination });
  };

  const toggleAutoScanner = () => {
    setAutoScannerIsOpen(!autoScannerIsOpen);
    setStepData(null); // Resetear siempre al abrir/cerrar
  };

  return (
    <Container maxWidth={false}>
      <Grid container spacing={3} className={classes.container}>
        <Grid item xs={12}>
          <Typography variant="h3" component="h2" className={classes.marginTop}>
            {t('Processes')}
          </Typography>
        </Grid>
        <Filters
          classes={classes}
          filter={filter}
          onFilterChange={onFilterChange}
          objects={objects}
          filterProcesses={filterProcesses}
          clearFilters={clearFilters}
          toggleEmployeeCostsModal={toggleEmployeeCostsModal}
          socketId={socketId}
          toggleAutoScanner={toggleAutoScanner}
          hasAutomation={hasAutomation}
          role={props.role}
        />
        <ItemList
          classes={classes}
          objects={objects}
          toggleEdit={toggleEdit}
          toggleDelete={toggleDelete}
          processList={processList}
          handleClick={handleClick}
          handleClose={handleClose}
          anchorEl={anchorEl}
          anchorElId={anchorElId}
          processOnClick={processOnClick}
          loaders={loaders}
          pagination={pagination}
          filter={filter}
          actions={actions}
          loadingProcess={loadingProcess}
          headerList={headerList}
          isMobileDevice={isMobileDevice}
          suppliersByProcess={suppliersByProcess}
          onOpenReclassifyModal={openReclassifyModal}
          role={props.role}
        />
      </Grid>
      <EmployeeCostsModal
        classes={classes}
        toggleModal={toggleEmployeeCostsModal}
        isOpen={employeeCostsModal}
        changeEmployeeCostsFile={changeEmployeeCostsFile}
        submitEmployeeCosts={submitEmployeeCosts}
        processListTotal={pagination.count}
        file={file}
        updatedProcesses={updatedProcesses}
        processes={objects.processes}
      />
      <ProcessForm
        isOpen={formIsOpen}
        toggleForm={toggleForm}
        changeControls={changeControls}
        onChange={actions.changeControls}
        controls={formControls}
        edit={edit}
        submit={submit}
        invalidControls={invalidControls}
        processTypes={objects.processTypes}
        workShiftTypes={objects.workShiftTypes}
        processCategories={objects.processCategories}
        receiptLots={objects.receiptLots}
        hasAutomation={hasAutomation}
      />
      <DeleteModal
        isOpen={deleteIsOpen}
        toggleForm={closeForm}
        submit={submitDelete}
        buttonId="delete-paddock"
      />

      <AutoScannModal
        open={autoScannerIsOpen}
        onClose={toggleAutoScanner}
        onScan={handleAutoScan}
        onSuccessRefresh={refreshProcessesAfterSuccess}
        objects={objects}
        stepData={stepData} // Le pasamos la pelota al hijo
        loadingExternal={loaders['execute-automatic-transition']}
      />
      <ReclassifySpeciesDispatchModal
        open={reclassifyModalOpen}
        onClose={closeReclassifyModal}
        onSubmit={submitReclassifySpeciesDispatch}
        process={reclassifyProcess}
        loading={loaders['reclassify-species-dispatch']}
      />

      {canCreate && (
        <Fab color="primary" aria-label="add" className={classes.fab} onClick={toggleForm}>
          <AddIcon />
        </Fab>
      )}
    </Container>
  );
}

export default ProcessesComponent;
